import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Send, Trash2, Zap, Bot, Link as LinkIcon, CheckCircle2, XCircle, EyeOff, Copy, Check, ChevronDown, ChevronUp, RotateCcw, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import { talentPosts, statuses, libraries } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../components/UI/LoadingSpinner';
import StatusSelect from '../components/UI/StatusSelect';
import { TextField, Autocomplete } from '@anyemp/ui-kit';
import TalentPostModal from '../components/TalentPost/TalentPostModal';
import PublishModal from '../components/TalentPost/PublishModal';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import Avatar from '../components/UI/Avatar';
import HistoryTab from '../components/UI/HistoryTab';

const BLOCK_LABELS = {
  responsibilities: 'Responsibilities',
  cv: 'CV / Experience',
  media: 'Media',
  languages: 'Languages',
  tools: 'Tools',
  professions: 'Professions'
};

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || fallback || '—';
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span className="min-w-0 break-all">{displayName}</span>
    </div>
  );
};

const TalentPostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Status editing
  const [statusRaw, setStatusRaw] = useState([]);
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  const [currenciesMap, setCurrenciesMap] = useState({}); // id → { name, iso3, symbol }

  // Generation
  const [revisions, setRevisions] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showRegenPanel, setShowRegenPanel] = useState(false);
  const [draft, setDraft] = useState(null); // текущий сгенерированный черновик
  const [savingDraft, setSavingDraft] = useState(false);

  const canUpdate = hasPermission('talent-service', 'talent_post', 'update');
  const canDelete = hasPermission('talent-service', 'talent_post', 'delete');

  useEffect(() => {
    fetchPost();
    loadStatuses();
    libraries.getCurrencies().then(res => {
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setCurrenciesMap(Object.fromEntries(list.map(c => [c.id, c])));
    }).catch(() => {});
  }, [id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await talentPosts.getByIdEnriched(id);
      setPost(res?.data || res);
    } catch (err) {
      toast.error('Failed to load post');
      navigate('/talent-posts');
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const statusesRes = await statuses.getStatusesByType('talent_post', { isCommon: 1 }).catch(() => ({ data: [] }));
      const statusList = Array.isArray(statusesRes?.data) ? statusesRes.data : (Array.isArray(statusesRes) ? statusesRes : []);
      setStatusRaw(statusList);
    } catch (err) {
      console.error('Failed to load statuses:', err);
    }
  };

  const loadModels = async () => {
    if (modelOptions.length > 0) return;
    try {
      const modelsRes = await talentPosts.getModels().catch(() => ({ data: [] }));
      const modelsList = Array.isArray(modelsRes?.data) ? modelsRes.data : (Array.isArray(modelsRes) ? modelsRes : []);
      setModelOptions(modelsList.map(m => ({ value: m.id, label: m.name || m.id })));
      const defaultModel = modelsList.find(m => m.id === 'google/gemini-2.0-flash-001');
      if (defaultModel) setSelectedModel(prev => prev || defaultModel.id);
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  };

  const handleStatusChange = async (statusId) => {
    setStatusChangeLoading(true);
    try {
      await talentPosts.update(id, { status_id: statusId || null });
      const newStatus = statusRaw.find(s => String(s.id) === String(statusId)) || null;
      setPost(prev => ({ ...prev, status_id: statusId || null, status: newStatus }));
      setEditingStatus(false);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleDeleteTerm = async (termType, termId) => {
    try {
      await talentPosts.deleteTermRecord(id, termType, termId);
      await fetchPost();
    } catch {
      toast.error('Failed to delete term');
    }
  };

  const handleGenerate = async (feedbackText = null) => {
    // Собрать revisions с feedback если есть
    let revisionsToSend = revisions;
    if (feedbackText && revisions.length > 0) {
      revisionsToSend = [
        ...revisions.slice(0, -1),
        { ...revisions[revisions.length - 1], feedback: feedbackText }
      ];
    }

    setGeneratingId(id);
    try {
      // Professions: берём profession_name из уже агрегированных postProfessions
      const resolvedProfessions = (post.postProfessions || [])
        .map(p => ({ profession_name: p.profession_name || '' }))
        .filter(p => p.profession_name);

      // Responsibilities: если is_similar — term_name, иначе responsibility_name
      const resolvedResponsibilities = (post.postResponsibilities || [])
        .map(r => ({ term_value: (r.is_similar ? r.term_name : r.responsibility_name) || '' }))
        .filter(r => r.term_value);

      const previewRes = await talentPosts.generate({
        talent: post.talentData ? { ...post.talent, ...post.talentData } : post.talent,
        inner_client: post.inner_client || null,
        translation: post.translation || null,
        blocks_config: post.blocks_config || {},
        professions: resolvedProfessions,
        responsibilities: resolvedResponsibilities,
        prompt_id: post.template?.prompt_id || null,
        tool_id: post.template?.tool_id || null,
        revisions: revisionsToSend,
        model: selectedModel || null
      });

      const generated = previewRes?.data || previewRes;
      const newDraft = generated?.full_post || '';

      const newRevisions = [
        ...(feedbackText && revisions.length > 0
          ? [...revisions.slice(0, -1), { ...revisions[revisions.length - 1], feedback: feedbackText }]
          : revisions),
        { draft: newDraft, feedback: null }
      ];
      setRevisions(newRevisions);
      setDraft(newDraft);
      setFeedback('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleAcceptDraft = async () => {
    if (!draft) return;
    setSavingDraft(true);
    try {
      await talentPosts.update(id, { full_post: draft });
      setPost(prev => ({ ...prev, full_post: draft }));
      setDraft(null);
      setRevisions([]);
      setFeedback('');
      toast.success('Post saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDiscardDraft = () => {
    setDraft(null);
    setRevisions([]);
    setFeedback('');
  };

  const handleCopy = () => {
    if (!post?.full_post) return;
    navigator.clipboard.writeText(post.full_post).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      await talentPosts.unpublish(id);
      setPost(prev => ({ ...prev, publish_status: 'unpublished' }));
      toast.success('Post unpublished');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unpublish');
    } finally {
      setUnpublishing(false);
    }
  };

  const handleDelete = async () => {
    setDeletingId(id);
    try {
      await talentPosts.remove(id);
      toast.success('Post deleted');
      navigate('/talent-posts');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
      setDeletingId(null);
    }
  };

  const handleResetProfessionPrices = async (professionId) => {
    try {
      const remaining = (post.postPrices || []).filter(p => p.profession_id !== professionId);
      if (remaining.length === 0) {
        await talentPosts.deletePrices(id);
      } else {
        await talentPosts.deletePrices(id);
        await talentPosts.savePrices(id, { prices: remaining.map(p => ({ profession_id: p.profession_id, rate_id: p.rate_id, price: p.price, currency_id: p.currency_id })) });
      }
      toast.success('Reset to profession');
      fetchPost();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset profession prices');
    }
  };

  if (loading) return <Loader />;
  if (!post) return null;

  const blocks = post.blocks_config || {};
  const isGenerating = generatingId === id || post.generation_status === 'pending';

  return (
    <div className="min-h-full bg-light-bg-primary p-4 dark:bg-dark-bg-primary sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/talent-posts')}
            className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <Send className="text-blue-600" size={24} />
          <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
            {post.name}
          </h1>
          {post.status && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: (post.status.color || '#6B7280') + '22', color: post.status.color || '#6B7280' }}
            >
              {post.status.name}
            </span>
          )}
          {post.publish_status === 'published' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle2 size={11} /> Published
            </span>
          ) : post.publish_status === 'unpublished' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              <EyeOff size={11} /> Unpublished
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-light-bg-tertiary text-light-text-tertiary dark:bg-dark-bg-tertiary dark:text-dark-text-tertiary">
              <XCircle size={11} /> Draft
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {post.publish_status === 'published' ? (
            <button
              onClick={() => setUnpublishConfirmOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-700 rounded-md hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
            >
              <EyeOff size={14} />
              Unpublish
            </button>
          ) : (
            <button
              onClick={() => setPublishModalOpen(true)}
              disabled={!post.full_post}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={14} />
              {post.publish_status === 'unpublished' ? 'Re-publish' : 'Publish'}
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              <Edit size={14} />
              Edit
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={!!deletingId}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-light-border-primary dark:border-dark-border-primary">
        {['details', 'history'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'history' && (
        <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
          <HistoryTab
            entityId={id}
            fetchEventsFunction={talentPosts.getEvents}
            fetchStatsFunction={talentPosts.getEventStats}
          />
        </div>
      )}

      {activeTab === 'details' && <>

      {/* Status inline editor */}
      <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">Status</span>
          <StatusSelect
            currentStatus={post.status}
            statusOptions={statusRaw}
            isEditing={editingStatus}
            isLoading={statusChangeLoading}
            onEdit={canUpdate ? () => setEditingStatus(true) : undefined}
            onCancel={() => setEditingStatus(false)}
            onChange={handleStatusChange}
          />
        </div>
      </div>

      {/* Info grid */}
      <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
        <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide mb-3">Info</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Talent</p>
            {post.talent ? (
              <Link
                to={`/talents/${post.talent.id}`}
                state={{ from: location.pathname + location.search }}
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {[post.talent.first_name, post.talent.last_name].filter(Boolean).join(' ') || `#${post.talent.id}`}
              </Link>
            ) : (
              <span className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
            )}
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Template</p>
            {post.template ? (
              <Link
                to={`/talent-templates/${post.template.id}`}
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {post.template.name}
              </Link>
            ) : (
              <span className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
            )}
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Translation</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {post.translation?.name || post.translation?.title || (post.translation_id ? `#${post.translation_id}` : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">—</span>)}
            </p>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Inner Client</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {post.inner_client?.name || (post.inner_client_id ? `#${post.inner_client_id}` : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">—</span>)}
            </p>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Planned Date</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {post.planned_date ? new Date(post.planned_date).toLocaleDateString() : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">—</span>}
            </p>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">End Date</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {post.end_date ? new Date(post.end_date).toLocaleDateString() : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">—</span>}
            </p>
          </div>

          {(post.publish_status === 'published' || post.publish_status === 'unpublished') && (
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3 col-span-2">
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Publish Date</p>
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  {post.publish_date ? new Date(post.publish_date).toLocaleString() : '—'}
                </p>
                {post.link && (
                  <a href={post.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline">
                    <LinkIcon size={11} /> {post.link}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Talent Profile Data */}
      {post.talentData && Object.values(post.talentData).some(v => Array.isArray(v) && v.length > 0) && (
        <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4 space-y-3">
          <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
            Talent Profile Data
          </p>

          {/* Languages */}
          {post.talentData.languages?.length > 0 && (
            <div>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">Languages</p>
              <div className="flex flex-wrap gap-1.5">
                {post.talentData.languages.map((lang, i) => {
                  const name = lang.language?.name || lang.language?.title || `#${lang.language_id}`;
                  const level = lang.level?.name || lang.level?.title || null;
                  return (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-700 text-sky-700 dark:text-sky-300">
                      {name}{level && <span className="opacity-60">· {level}</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tools */}
          {post.talentData.tools?.length > 0 && (
            <div>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">Tools</p>
              <div className="flex flex-wrap gap-1.5">
                {post.talentData.tools.map((t, i) => {
                  const name = t.tool?.name || t.tool?.title || `#${t.tool_id}`;
                  return (
                    <span key={i} className="inline-flex items-center px-2.5 py-1 text-xs rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300">
                      {name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* CV/Experience */}
          {post.talentData.cv?.length > 0 && (
            <div>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">CV / Experience</p>
              <div className="space-y-1.5">
                {post.talentData.cv.map((entry, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs bg-light-bg-secondary dark:bg-dark-bg-secondary rounded px-3 py-2">
                    <div className="flex-1">
                      <span className="font-medium text-light-text-primary dark:text-dark-text-primary">{entry.company_name}</span>
                      {entry.specialisation && <span className="text-light-text-secondary dark:text-dark-text-secondary ml-1">— {entry.specialisation}</span>}
                    </div>
                    {(entry.start_date || entry.end_date) && (
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary whitespace-nowrap shrink-0">
                        {entry.start_date ? new Date(entry.start_date).getFullYear() : '?'}
                        {' – '}
                        {entry.end_date ? new Date(entry.end_date).getFullYear() : 'now'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Media */}
          {post.talentData.media?.length > 0 && (
            <div>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">Media</p>
              <div className="space-y-1.5">
                {post.talentData.media.map((m, i) => {
                  const url = m.file_url || m.file_path;
                  const fileName = m.file_name || (m.file_path ? m.file_path.split('/').pop() : null) || `file-${i + 1}`;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs bg-light-bg-secondary dark:bg-dark-bg-secondary rounded px-3 py-2">
                      {m.media_type && (
                        <span className="shrink-0 capitalize px-1.5 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-tertiary dark:text-dark-text-tertiary">
                          {m.media_type}
                        </span>
                      )}
                      {m.mime_type && (
                        <span className="shrink-0 text-light-text-tertiary dark:text-dark-text-tertiary">
                          {m.mime_type}
                        </span>
                      )}
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer"
                          className="flex-1 truncate text-emerald-600 dark:text-emerald-400 hover:underline">
                          {fileName}
                        </a>
                      ) : (
                        <span className="flex-1 truncate text-light-text-secondary dark:text-dark-text-secondary">{fileName}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recipient (Account / Social Account) */}
      {(post.accountData || post.socialAccountData) && (
        <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4 space-y-2">
          <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
            Recipient
          </p>
          {post.accountData && (
            <div className="flex items-center gap-3 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md px-3 py-2">
              <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary w-24 shrink-0">Account</span>
              <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                {post.accountData.name || post.accountData.email || `#${post.account_id}`}
              </span>
              {post.accountData.tool?.name && (
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">({post.accountData.tool.name})</span>
              )}
            </div>
          )}
          {post.socialAccountData && (
            <div className="flex items-center gap-3 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md px-3 py-2">
              <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary w-24 shrink-0">Social Account</span>
              <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                {post.socialAccountData.name || post.socialAccountData.username || `#${post.social_account_id}`}
              </span>
              {post.socialAccountData.tool?.name && (
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">({post.socialAccountData.tool.name})</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content Blocks */}
      <div className="mb-4 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4">
        <p className="text-xs font-medium text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider mb-3">
          Content Blocks
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {Object.entries(BLOCK_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-sm shrink-0 ${blocks[key] ? 'bg-green-500' : 'bg-light-border-primary dark:bg-dark-border-primary'}`} />
              <span className={`text-sm ${blocks[key] ? 'text-light-text-primary dark:text-dark-text-primary' : 'text-light-text-tertiary dark:text-dark-text-tertiary line-through'}`}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Terms: Professions & Responsibilities */}
      {((post.postProfessions?.length > 0) || (post.postResponsibilities?.length > 0)) && (
        <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
          <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide mb-3">
            AI Terms Used
          </p>
          <div className="space-y-3">
            {post.postProfessions?.length > 0 && (
              <div>
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">Professions</p>
                <div className="flex flex-wrap gap-1.5">
                  {post.postProfessions.map((p, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border ${p.is_primary ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500 text-blue-800 dark:text-blue-200 font-semibold' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'}`}>
                      {p.is_primary && <span className="text-[10px] text-blue-500 dark:text-blue-300">★</span>}
                      {p.is_similar && <span className="text-[10px] opacity-60">~</span>}
                      {p.profession_name || `#${p.talent_profession_id}`}
                      {canUpdate && (
                        <button onClick={() => handleDeleteTerm('profession', p.id)} className="ml-0.5 opacity-40 hover:opacity-100 text-red-500" title="Remove">
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {post.postResponsibilities?.length > 0 && (
              <div>
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">Responsibilities</p>
                <div className="flex flex-wrap gap-1.5">
                  {post.postResponsibilities.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-300">
                      {r.is_similar && <span className="text-[10px] opacity-60">~</span>}
                      {r.is_similar ? (r.term_name || r.responsibility_name) : r.responsibility_name}
                      {canUpdate && (
                        <button onClick={() => handleDeleteTerm('responsibility', r.id)} className="ml-0.5 opacity-40 hover:opacity-100 text-red-500" title="Remove">
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Prices */}
      {(() => {
        // Группируем цены по профессиям
        // Структура: [{ professionName, isPrimary, isCustom, rows: [{rate, price, currency_id, currency}] }]
        const profGroups = [];
        // Строим map кастомных цен по profession_id
        const customPriceMap = {};
        for (const sp of (post.postPrices || [])) {
          if (!customPriceMap[sp.profession_id]) customPriceMap[sp.profession_id] = [];
          customPriceMap[sp.profession_id].push(sp);
        }

        for (const pp of (post.postProfessions || [])) {
          const prof = pp.profession;
          const customRows = customPriceMap[pp.profession_id];
          if (customRows?.length) {
            profGroups.push({
              professionId: pp.profession_id,
              professionName: pp.profession_name,
              isPrimary: pp.is_primary ?? false,
              isCustom: true,
              rows: customRows.map(sp => ({
                rate: sp.rate,
                rate_id: sp.rate_id,
                price: sp.price ?? null,
                currency_id: sp.currency_id ?? null,
                currency: sp.currency ?? null
              }))
            });
          } else if (prof?.rates?.length) {
            profGroups.push({
              professionId: pp.profession_id,
              professionName: pp.profession_name,
              isPrimary: pp.is_primary ?? false,
              isCustom: false,
              rows: prof.rates.map(rate => ({
                rate,
                rate_id: rate.id,
                price: rate.profession_rate?.price ?? null,
                currency_id: prof.currency_id ?? null,
                currency: prof.currency ?? null
              }))
            });
          }
        }

        if (!profGroups.length) return null;

        const discount = post.discount != null ? parseFloat(post.discount) : null;

        return (
          <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
                Prices
              </p>
            </div>
            {discount != null && (
              <div className="mb-2 flex items-center gap-1.5">
                <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${discount >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                  {discount >= 0 ? `−${post.discount}%` : `+${Math.abs(discount)}%`}
                </span>
                <span className="text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">discount applied</span>
              </div>
            )}
            <div className="space-y-3">
              {profGroups.map((group, gi) => (
                <div key={gi} className="rounded-lg border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
                  <div className="px-3 py-1.5 bg-light-bg-secondary dark:bg-dark-bg-tertiary flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
                        {group.professionName}
                      </span>
                      {group.isPrimary && (
                        <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400">★ Primary</span>
                      )}
                    </div>
                    {group.isCustom && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] font-semibold text-amber-500 dark:text-amber-400">Custom</span>
                        {canUpdate && (
                          <button
                            type="button"
                            onClick={() => handleResetProfessionPrices(group.professionId)}
                            className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary rounded hover:bg-light-bg-primary dark:hover:bg-dark-bg-primary transition-colors"
                          >
                            <RotateCcw size={9} />
                            Reset to Profession
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
                    {group.rows.map((p, i) => {
                      const rateName = p.rate?.name || `Rate #${p.rate_id}`;
                      const rateHours = p.rate?.hours ? ` (${p.rate.hours}h)` : '';
                      const currObj = currenciesMap[p.currency_id] || p.currency || null;
                      const currencyCode = currObj?.iso3 || currObj?.symbol || currObj?.name || (p.currency_id ? `#${p.currency_id}` : '');
                      const finalPrice = p.price != null && discount != null
                        ? parseFloat(p.price) * (1 - discount / 100)
                        : null;
                      return (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                            {rateName}{rateHours}
                          </span>
                          <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                            {p.price != null ? (
                              finalPrice != null ? (
                                <span className="flex items-center gap-2">
                                  <span className="line-through text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                                    {parseFloat(p.price).toLocaleString()}
                                  </span>
                                  <span>
                                    {finalPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    {currencyCode && <span className="ml-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{currencyCode}</span>}
                                  </span>
                                </span>
                              ) : (
                                <>{parseFloat(p.price).toLocaleString()}{currencyCode && <span className="ml-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{currencyCode}</span>}</>
                              )
                            ) : (
                              <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Full Post */}
      <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-light-border-primary bg-light-bg-secondary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-tertiary">
          <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
            Generated Post
          </p>
          <div className="flex items-center gap-2">
            {post.full_post && !draft && (
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-primary dark:hover:bg-dark-bg-primary transition-colors"
              >
                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            {!draft && (
              <button
                onClick={() => { setShowRegenPanel(v => !v); loadModels(); }}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-primary dark:hover:bg-dark-bg-primary transition-colors"
              >
                <Zap size={12} />
                {post.full_post ? 'Re-generate' : 'Generate'}
                {showRegenPanel ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            )}
          </div>
        </div>

        {/* Generate panel — collapsible, only when no draft */}
        {showRegenPanel && !draft && (
          <div className="px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary bg-purple-50 dark:bg-purple-900/10 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              {modelOptions.length > 0 && (
                <div className="flex items-center gap-2 flex-1 min-w-48">
                  <Bot size={12} className="text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
                  <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary whitespace-nowrap">Model</span>
                  <div className="flex-1">
                    <Autocomplete
                      clearable
                      fullWidth
                      options={modelOptions}
                      value={selectedModel}
                      onChange={val => setSelectedModel(val || null)}
                      placeholder="Default"
                    />
                  </div>
                </div>
              )}
              <button
                onClick={() => handleGenerate()}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors shrink-0"
              >
                {isGenerating ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                {post.full_post ? 'Re-generate' : 'Generate'}
              </button>
            </div>
          </div>
        )}

        {/* Draft review panel */}
        {draft && (
          <div className="border-b border-light-border-primary dark:border-dark-border-primary">
            {/* Draft content */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                  AI Draft {revisions.length > 1 && `(revision ${revisions.length})`}
                </span>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{draft}</ReactMarkdown>
              </div>
            </div>
            {/* Feedback + actions */}
            <div className="px-4 py-3 bg-light-bg-secondary dark:bg-dark-bg-tertiary space-y-3">
              <TextField
                multiline
                rows={2}
                size="small"
                fullWidth
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Feedback for regeneration (optional)..."
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleAcceptDraft}
                  disabled={savingDraft}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {savingDraft ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  Accept & Save
                </button>
                <button
                  onClick={() => handleGenerate(feedback || null)}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {isGenerating ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                  Regenerate
                </button>
                <button
                  onClick={handleDiscardDraft}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-light-text-secondary dark:text-dark-text-secondary border border-light-border-primary dark:border-dark-border-primary rounded-md hover:bg-light-bg-primary dark:hover:bg-dark-bg-primary transition-colors"
                >
                  <XCircle size={14} />
                  Discard
                </button>
                {modelOptions.length > 0 && (
                  <div className="flex items-center gap-2 flex-1 min-w-40">
                    <Bot size={12} className="text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
                    <div className="flex-1">
                      <Autocomplete
                        clearable
                        fullWidth
                        options={modelOptions}
                        value={selectedModel}
                        onChange={val => setSelectedModel(val || null)}
                        placeholder="Default model"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Saved content */}
        {!draft && (
          <div className="p-4">
            {post.full_post ? (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{post.full_post}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary italic">
                Not generated yet — click Generate to create content
              </p>
            )}
          </div>
        )}
      </div>

      {/* Dates & Author */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created by</span>
          <div className="min-w-0 max-w-full text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <UserCell user={post.createdByUser} fallback={post.created_by} />
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated by</span>
          <div className="min-w-0 max-w-full text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <UserCell user={post.updatedByUser} fallback={post.updated_by} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created</span>
          <span className="min-w-0 break-words text-right text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {post.created_at ? new Date(post.created_at).toLocaleString() : '—'}
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated</span>
          <span className="min-w-0 break-words text-right text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {post.updated_at ? new Date(post.updated_at).toLocaleString() : '—'}
          </span>
        </div>
      </div>

      </>}

      <TalentPostModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        post={post}
        onSaved={() => { setModalOpen(false); fetchPost(); }}
      />
      <PublishModal
        isOpen={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        post={post}
        onPublished={() => { setPublishModalOpen(false); fetchPost(); }}
      />
      <ConfirmDialog
        isOpen={unpublishConfirmOpen}
        onClose={() => setUnpublishConfirmOpen(false)}
        onConfirm={handleUnpublish}
        title="Unpublish Post"
        message="Unpublish this post?"
        confirmText="Unpublish"
        confirmStyle="warning"
        isLoading={unpublishing}
      />
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Talent Post"
        message="Delete this post?"
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={!!deletingId}
      />
    </div>
  );
};

export default TalentPostDetail;
